// Utilidades compartidas del módulo Catering (recetario, menús cíclicos, producción, contratos).
// CRUD directo a PostgREST vía /rest/catering.<tabla> + RPCs del esquema catering.
import type { QueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';

const base = (t: string) => `/rest/catering.${t}`;
const repr = { headers: { Prefer: 'return=representation' } };

export const cat = {
  list: async <T,>(t: string, query = ''): Promise<T[]> =>
    (await apiClient.get<T[]>(`${base(t)}${query}`)).data,
  create: async (t: string, body: unknown) =>
    (await apiClient.post(base(t), body, repr)).data,
  update: async (t: string, id: string, body: unknown) =>
    (await apiClient.patch(`${base(t)}?id=eq.${id}`, body, repr)).data,
  remove: async (t: string, id: string) =>
    (await apiClient.delete(`${base(t)}?id=eq.${id}`)).data,
};

export const CAT = { headers: { 'Content-Profile': 'catering', 'Accept-Profile': 'catering' } };

export const catRpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, CAT)).data;

// Invalida catering + inventario (las OP mueven stock/kardex)
export const invalidateCatering = (qc: QueryClient): void => {
  void qc.invalidateQueries({
    predicate: (q) =>
      typeof q.queryKey[0] === 'string' &&
      ((q.queryKey[0] as string).startsWith('cat-') || (q.queryKey[0] as string).startsWith('inv-')),
  });
};

// ---- Tipos ----
export interface RecetaCosto {
  receta_id: string; porciones_std: number; costo_batch: number; costo_porcion: number;
  alergenos: string[]; sin_costo: { id: string; nombre: string }[];
}
export interface RecetaResumen {
  id: string; producto_id: string; nombre: string; tipo: 'subproducto' | 'plato';
  categoria: string | null; porciones_std: number;
  rendimiento_cantidad: number; rendimiento_unidad: string;
  alergenos: string[]; elaborado_por: string | null; version: number; es_activo: boolean;
  num_lineas: number; costo: RecetaCosto;
}
export interface RecetaLineaDet {
  id: string; componente_id: string; nombre: string; cantidad: number; unidad: string;
  merma_cant: number; costo_ref: number | null; orden: number; notas: string | null;
  subreceta_id: string | null; costo_linea: number;
}
export interface RecetaDetalle extends Omit<RecetaResumen, 'num_lineas'> {
  instrucciones: string | null; lineas: RecetaLineaDet[];
}
export interface CicloItem {
  id: string; dia: number; servicio: string; orden: number;
  receta_id: string; nombre: string; tipo: string; costo_porcion: number; sin_costo: number;
}
export interface Ciclo { id: string; nombre: string; orden: number; es_activo: boolean }
export interface Contrato {
  id: string; codigo: string | null; partner_id: string; nombre: string; ciclo_id: string | null;
  fecha_inicio: string; fecha_fin: string | null; corte: string; es_activo: boolean;
}
export interface ContratoPrecio { id: string; contrato_id: string; servicio: string; precio: number; pax_pactado: number }
export interface Efectivo {
  id: string; contrato_id: string; fecha: string; servicio: string;
  cantidad_plan: number; cantidad_servida: number | null;
}
export interface OpResumen {
  id: string; codigo: string; fecha: string; estado: string; bodega_mp: string;
  porciones_plan: number; porciones_reales: number; costo_mp: number;
}
export interface OpPlato {
  servicio: string; receta_id: string; nombre: string;
  porciones_plan: number; porciones_reales: number | null; costo_porcion: number;
}
export interface OpProduccion { receta_id: string; nombre: string; porciones_plan: number; porciones_reales: number | null }
export interface OpConsumo {
  material_id: string; nombre: string; unidad: string | null; presentacion: string | null;
  cantidad_plan: number; cantidad_consumida: number; costo_unit: number; stock: number;
}
export interface OpDetalle {
  id: string; codigo: string; fecha: string; estado: string;
  bodega_mp_id: string; bodega_mp: string; observaciones: string | null;
  porciones_plan: number; porciones_reales: number; costo_mp: number;
  platos: OpPlato[]; produccion: OpProduccion[]; consumos: OpConsumo[];
}
export interface CompraSugerida {
  material_id: string; nombre: string; unidad: string | null; presentacion: string | null;
  costo_ref: number; necesidad: number; stock: number; comprar: number; costo_compra: number;
}

export const SERVICIOS = ['desayuno', 'almuerzo_1', 'almuerzo_2', 'dieta', 'merienda', 'cena', 'break'] as const;
export const SERVICIO_LABEL: Record<string, string> = {
  desayuno: 'Desayuno', almuerzo_1: 'Almuerzo Op. 1', almuerzo_2: 'Almuerzo Op. 2',
  dieta: 'Dieta', merienda: 'Merienda', cena: 'Cena', break: 'Break',
};
export const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

export const OP_ESTADO_LABEL: Record<string, string> = {
  borrador: 'Borrador', en_proceso: 'En proceso', cerrada: 'Cerrada', anulada: 'Anulada',
};
export const OP_ESTADO_CLS: Record<string, string> = {
  borrador: 'bg-muted text-muted-foreground',
  en_proceso: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  cerrada: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  anulada: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

// ---- Piso de cocina ----
export interface PisoBatch {
  id: string; codigo: string; receta: string; receta_id: string;
  estacion: string; estacion_codigo: string; equipo: string;
  estado: string; sub_indice: number; sub_total: number;
  porciones_plan: number; porciones_reales: number | null;
  hora_servicio: string | null; latest_start: string | null; duracion_min: number;
  iniciado_at: string | null; listo_at: string | null;
  temperatura_c: number | null; temp_min_c: number | null;
  responsable: string | null; motivo: string | null;
  alergenos: string[]; es_dieta: boolean;
  holgura_min: number | null; fin_proyectado: string | null;
  semaforo: 'verde' | 'ambar' | 'rojo' | 'detenido' | 'sanitario' | 'ok';
  peligro_min: number | null; peligro_semaforo: 'ok' | 'alarma' | 'vencido' | null;
  es_critico: boolean;
}
export interface PisoResumen {
  fecha: string; ahora: string; proximo_servicio: string | null; minutos_al_servicio: number | null;
  total_batches: number; despachados: number; en_curso: number; pendientes: number;
  detenidos: number; en_rojo: number; en_ambar: number; sanitario: number;
  porciones_plan: number; porciones_despachadas: number;
  estado_global: 'en_ritmo' | 'ajustado' | 'atrasado' | 'sanitario';
}
export interface PisoAlerta { prioridad: string; tipo: string; batch_id: string; codigo: string; texto: string }
export interface PisoTablero {
  resumen: PisoResumen;
  batches: PisoBatch[];
  estaciones: { codigo: string; nombre: string; pendientes: number; en_rojo: number }[];
  cuello: { equipo: string; unidades: number; demanda: number } | null;
  alertas: PisoAlerta[];
}
export interface CargaEquipo {
  equipo: string; unidades: number; solapes: number;
  barras: { id: string; codigo: string; receta: string; estado: string; inicio: string; fin: string }[];
}
export interface Estacion {
  id: string; codigo: string; nombre: string; orden: number; estados: string[];
  color: string | null; es_activo: boolean;
}
export interface Equipo {
  id: string; codigo: string; nombre: string; tipo: string; estacion_id: string | null;
  unidades: number; capacidad_porciones: number | null; es_activo: boolean;
}
export interface ServicioHora { id: string; contrato_id: string | null; servicio: string; hora: string }
export interface PisoPantalla {
  id: string; token: string; nombre: string; estacion_id: string | null;
  last_seen: string | null; es_activo: boolean;
}

export const PISO_ESTADO_LABEL: Record<string, string> = {
  planificado: 'Planificado', mise_en_place: 'Mise en place', en_proceso: 'En cocción',
  listo: 'Listo / mantención', porcionado: 'Porcionado', despachado: 'Despachado',
  detenido: 'DETENIDO', retenido: 'RETENIDO (calidad)',
};

// Secuencia por defecto; cada estación puede tener la suya
export const PISO_SECUENCIA = ['planificado', 'mise_en_place', 'en_proceso', 'listo', 'porcionado', 'despachado'];

export const siguienteEstado = (actual: string, secuencia: string[] = PISO_SECUENCIA): string | null => {
  const i = secuencia.indexOf(actual);
  return i >= 0 && i < secuencia.length - 1 ? secuencia[i + 1] : null;
};

// Colores del semáforo (borde + fondo), pensados para leerse de lejos
export const SEMAFORO_CLS: Record<string, string> = {
  verde: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
  ambar: 'border-amber-500 bg-amber-50 dark:bg-amber-900/20',
  rojo: 'border-red-600 bg-red-50 dark:bg-red-900/25',
  detenido: 'border-purple-600 bg-purple-50 dark:bg-purple-900/25',
  sanitario: 'border-sky-600 bg-sky-50 dark:bg-sky-900/25',
  ok: 'border-muted bg-muted/40 opacity-70',
};
export const SEMAFORO_TXT: Record<string, string> = {
  verde: 'text-emerald-700 dark:text-emerald-400',
  ambar: 'text-amber-700 dark:text-amber-400',
  rojo: 'text-red-700 dark:text-red-400',
  detenido: 'text-purple-700 dark:text-purple-400',
  sanitario: 'text-sky-700 dark:text-sky-400',
  ok: 'text-muted-foreground',
};
export const ESTADO_GLOBAL_LABEL: Record<string, string> = {
  en_ritmo: 'EN RITMO', ajustado: 'AJUSTADO', atrasado: 'ATRASADO', sanitario: 'ALERTA SANITARIA',
};
export const ESTADO_GLOBAL_CLS: Record<string, string> = {
  en_ritmo: 'bg-emerald-600 text-white',
  ajustado: 'bg-amber-500 text-white',
  atrasado: 'bg-red-600 text-white',
  sanitario: 'bg-sky-600 text-white',
};
export const MOTIVOS_DETENCION = ['Falta MP', 'Falta equipo', 'Falta personal', 'Cambio de pax', 'Calidad'];

// hh:mm de un timestamptz, en hora local
export const hhmm = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};
// Holgura en formato humano: +1h20 / −45m
export const holguraTxt = (min: number | null | undefined): string => {
  if (min == null) return '—';
  const s = min < 0 ? '−' : '+';
  const a = Math.abs(Math.round(min));
  return a >= 60 ? `${s}${Math.floor(a / 60)}h${String(a % 60).padStart(2, '0')}` : `${s}${a}m`;
};

export const money = (v: number | string | null | undefined, d = 4): string => {
  const n = typeof v === 'string' ? parseFloat(v) : (v ?? 0);
  return `$${(Number.isFinite(n) ? n : 0).toFixed(d)}`;
};
